import { User } from "oidc-client-ts";
import { configurationHelper } from "../configurationHelper";

export interface Problem {
  title: string;
  type?: string;
  instance?: string;
}

export type formErrors = { [key: string]: string[] };

export interface ValidationProblem extends Problem {
  validation: formErrors;
}

export function TrimValidationFields(problem: Problem, acceptedPrefix: string) {
  if (!("validation" in problem)) {
    return;
  }

  let validationProblem = problem as ValidationProblem;

  let cleanedValidation: formErrors = {};

  acceptedPrefix += ".";

  for (let key in validationProblem.validation) {
    let newKey = key;

    if (newKey.startsWith(acceptedPrefix)) {
      newKey = newKey.replace(acceptedPrefix, "");
      newKey = newKey[0].toLowerCase() + newKey.substring(1);
    } else {
      newKey = "";
    }

    if (!cleanedValidation[newKey]) cleanedValidation[newKey] = [];

    cleanedValidation[newKey] = cleanedValidation[newKey].concat(
      validationProblem.validation[key],
    );
  }

  validationProblem.validation = cleanedValidation;
}

export type Result<T, P = Problem> =
  | { ok: true; value: T }
  | { ok: false; problem: P };

function getUser() {
  // get the user from storage
  const id = `oidc.user:${configurationHelper.get(
    "OPENID_AUTHORITY",
  )}:${configurationHelper.get("OPENID_CLIENT_ID")}`;

  let oidcStorage = sessionStorage.getItem(id);
  if (!oidcStorage) {
    // try localStorage
    oidcStorage = localStorage.getItem(id);
  }

  if (!oidcStorage) return undefined;

  return User.fromStorageString(oidcStorage);
}

type BlobProperties = {
  /**
   * "The returning blob's mediatype like application/pdf"
   */
  MediaType: string | undefined;
  /**
   * The returnings blob's file name like mypdf.pfd
   */
  FileName: string;
};

export const backendApi = {
  baseUrl: configurationHelper.get("API_URL"),

  async internalSendAsync(
    name: string,
    request: any,
  ): Promise<Result<Response>> {
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    let token = getUser()?.access_token;

    if (token) headers.append("Authorization", "Bearer " + token);

    try {
      let rawResponse = await window.fetch(`${this.baseUrl}/${name}`, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(request),
      });

      if (rawResponse.status === 200) return { ok: true, value: rawResponse };

      let problem = rawResponse.headers
        .get("Content-Type")
        ?.startsWith("application/problem")
        ? ((await rawResponse.json()) as Problem)
        : undefined;

      if (problem) {
        return { ok: false, problem: problem };
      }

      return {
        ok: false,
        problem: {
          title: `Unexpected error sending request: ${rawResponse.status} ${rawResponse.statusText}.`,
        },
      };
    } catch {}

    return {
      ok: false,
      problem: { title: "Unexpected error connecting to the backend." },
    };
  },

  async sendAsync(name: string, request: any): Promise<Result<any>> {
    let result = await this.internalSendAsync(name, request);

    return result;
  },

  /**
   * @param name relative uri
   * @param request request Body
   * @returns result wrapped in a promise
   */
  async sendAndGetResponseAsync<TResponse>(
    name: string,
    request: any,
  ): Promise<Result<TResponse>> {
    let response = await this.internalSendAsync(name, request);

    if (!response.ok) return response;

    try {
      return { ok: true, value: (await response.value.json()) as TResponse };
    } catch {}

    return {
      ok: false,
      problem: { title: "Unexpected error reading the response" },
    };
  },

  /**
   * @param name relative uri
   * @param request request Body
   * @returns result wrapped in a promise
   */
  async sendAndGetRawResponseAsync(
    name: string,
    request: any,
  ): Promise<Result<Response>> {
    let response = await this.internalSendAsync(name, request);

    if (!response.ok) return response;

    try {
      return { ok: true, value: response.value };
    } catch {}

    return {
      ok: false,
      problem: { title: "Unexpected error reading the response" },
    };
  },

  async uploadFileAsync(
    url: string,
    file: File,
    filename: string,
  ): Promise<Result<any>> {
    const headers = new Headers();
    headers.append("Accept", "application/json");

    let token = getUser()?.access_token;

    if (token) headers.append("Authorization", "Bearer " + token);

    try {
      let data = new FormData();
      data.append("file", file, filename);

      let rawResponse = await window.fetch(`${this.baseUrl}/uploads/${url}`, {
        method: "POST",
        headers: headers,
        body: data,
      });

      if (rawResponse.status === 200) return { ok: true, value: rawResponse };

      let problem = rawResponse.headers
        .get("Content-Type")
        ?.startsWith("application/problem")
        ? ((await rawResponse.json()) as Problem)
        : undefined;

      if (problem) {
        return { ok: false, problem: problem };
      }
    } catch {}

    return {
      ok: false,
      problem: { title: "Unexpected error sending request" },
    };
  },

  async uploadFileWithResponseAsync<TResponse>(
    url: string,
    file: File,
    filename: string,
  ): Promise<Result<TResponse>> {
    const result = await this.uploadFileAsync(url, file, filename);

    if (!result.ok) return result;

    try {
      return { ok: true, value: (await result.value.json()) as TResponse };
    } catch {}

    return {
      ok: false,
      problem: { title: "Unexpected error sending request" },
    };
  },

  async downloadFileAsync(url: string): Promise<Result<any>> {
    // does a post on a new tab
    // the jwt token is passed as a form field instead of a header
    let form = document.createElement("form");
    form.method = "post";
    form.target = "_blank";
    form.action = `${this.baseUrl}/downloads/${url}`;

    let token = getUser()?.access_token;

    if (token) {
      form.innerHTML =
        '<input type="hidden" name="jwtToken" value="' + token + '">';
    }

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);

    return { ok: true, value: true };
  },

  /**
   * @param url same "url" as downloadFileAsync; it is not a full uri; only relative path
   * @param blobProperties propperies for the returning Blob
   * @returns
   */
  async downloadBlobFile(
    url: string,
    blobProperties: BlobProperties | null = null,
  ) {
    let imageReponseResult = await this.sendAndGetRawResponseAsync(
      `downloads/${url}`,
      {},
    );
    if (imageReponseResult.ok) {
      const response = imageReponseResult.value;
      if (blobProperties === null) {
        return await response.blob();
      } else {
        const blob = await response.arrayBuffer();
        return new File([blob], blobProperties.FileName, {
          type: blobProperties.MediaType,
        });
      }
    }
    return null;
  },
};
